/**
 * Builds the initial Formik values for the workers-request-form.
 *
 * Handles four distinct scenarios:
 * 1. Editing an existing workers request (`props.workersRequest`).
 * 2. Creating a new workers request from a mission template (`fromTemplate.mission`).
 * 3. Creating a new workers request from another workers request template
 *    (`fromTemplate.workersRequest`).
 * 4. Creating a blank new workers request (fallback).
 */
import { addDays, differenceInDays, nextSunday } from 'date-fns'
import { formatISODate } from '../../../../../utils/date'
import {
  WorkersRequestCreateFormValues,
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormProps,
  WorkersRequestUpdateFormValues,
} from '../workers-request-form.types'

/**
 * Default shape for a brand new (draft) post row in the form.
 */
export const newPostTemplate: WorkersRequestCreateFormValues['posts'][0] = Object.freeze({
  count: 1,
  jobTitle: '',
  recourseCase: {
    reason: '',
    justification: '',
  },
  desiredWorkers: [],
  description: '',
  tags: [],
})

/**
 * Computes the initial values for the form.
 *
 * @param props - The form component props (workersRequest, fromTemplate, ...).
 * @returns Initial Formik values matching the scenario inferred from props.
 */
export const getWorkersRequestFormInitialValues = (
  props: WorkersRequestFormProps,
): WorkersRequestCreateUpdateFormValues => {
  const { workersRequest, fromTemplate } = props

  // Default date window used for creation scenarios: today -> next sunday,
  // extended to the following sunday if less than 3 days away.
  const now = new Date()
  const today = now
  const todayISO = formatISODate(now)
  const sunday = nextSunday(now)
  const end = differenceInDays(sunday, today) <= 3 ? addDays(sunday, 7) : sunday
  const endISO = formatISODate(end)

  // 1. Editing an existing workers request.
  if (workersRequest) {
    const firstWorkPeriodStartDate = workersRequest.missionsStart
    const startDateISO = formatISODate(new Date(firstWorkPeriodStartDate))
    const lastWorkPeriodStartDate = workersRequest.missionsEnd
    const endDateISO = formatISODate(new Date(lastWorkPeriodStartDate))

    const initialValues: WorkersRequestUpdateFormValues = {
      meta: {
        startDate: startDateISO,
        endDate: endDateISO,
        resource: workersRequest,
      },
      interimAgency: workersRequest.interimAgency._id,
      missionData: {
        workPeriods: workersRequest.missionData.workPeriods.map(wP => ({
          ...wP,
          start: { date: new Date(wP.start.date) },
          end: { date: new Date(wP.end.date) },
        })),
        service: workersRequest?.missionData.service?._id ?? '',
        address: workersRequest?.missionData.address,
        complementaryInformations: workersRequest.missionData.complementaryInformations ?? '',
      },
      posts: workersRequest.posts.map(post => {
        return {
          ...post,
          desiredWorkers: post.desiredWorkers.map(dW => dW._id.toString()),
          jobTitle: post.jobTitle._id,
          count: 1,
          tags: post.tags || [],
        }
      }),
    }
    return initialValues
  }

  // 2. Creating a new workers request from a mission template.
  if (fromTemplate?.mission) {
    const { mission } = fromTemplate
    const startDateISO = todayISO
    const endDateISO = endISO

    const initialValues: WorkersRequestCreateFormValues = {
      meta: {
        startDate: startDateISO,
        endDate: endDateISO,
        resource: undefined,
      },
      interimAgency: mission.interimAgency?._id ?? '',
      missionData: {
        workPeriods: [],
        service: mission.service?._id ?? '',
        address: mission.address,
        complementaryInformations: mission.complementaryInformations ?? '',
      },
      posts: [
        {
          ...newPostTemplate,
          jobTitle: mission.jobTitle._id.toString(),
          recourseCase: mission.workersRequestOfMission.postOfMission.recourseCase,
          description: mission.workersRequestOfMission.postOfMission.description,
          desiredWorkers: [mission.user._id],
          tags: mission.workersRequestOfMission.postOfMission.tags || [],
        },
      ],
    }
    return initialValues
  }

  // 3. Creating a new workers request from another workers request template.
  if (fromTemplate?.workersRequest) {
    const { workersRequest } = fromTemplate
    const startDateISO = todayISO
    const endDateISO = endISO

    const posts: WorkersRequestCreateFormValues['posts'] = workersRequest.posts
      // Strip the posts to only the fields needed by the form.
      .map(p => {
        const post = {
          count: 1,
          desiredWorkers: p.desiredWorkers.map(dW => dW._id.toString()),
          jobTitle: p.jobTitle._id,
          recourseCase: p.recourseCase,
          description: p.description ?? '',
          tags: p.tags || [],
        }
        const lastMissionOfPost = p.missions?.[p.missions.length - 1]
        if (lastMissionOfPost && !lastMissionOfPost.isCancelled && !lastMissionOfPost.isAbandoned) {
          post.desiredWorkers.push(lastMissionOfPost.user._id.toString())
        }
        return post
      })
      // Fold identical consecutive posts together (same recourseCase/jobTitle/description/tags).
      // For folded posts, count is incremented and desiredWorkers merged (deduplicated).
      .reduce(
        (acc, current) => {
          const previous = structuredClone(acc.pop())
          if (previous) {
            // Exclude `count` and `desiredWorkers` from the comparison: two posts are
            // considered identical if all other fields match.
            const { desiredWorkers: previousDesiredWorkers, ...previousRest } = previous
            const { desiredWorkers: currentDesiredWorkers, ...currentRest } = current
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { count: _prevCount, ...previousWithoutCount } = previousRest
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { count: _currCount, ...currentWithoutCount } = currentRest
            if (JSON.stringify(previousWithoutCount) === JSON.stringify(currentWithoutCount)) {
              previous.count = previous.count + 1
              previous.desiredWorkers = [...previousDesiredWorkers, ...currentDesiredWorkers]
                .filter((id): id is string => !!id)
                .filter((id, index, all) => all.indexOf(id) === index)
              return [...acc, previous]
            } else {
              previous.desiredWorkers = [...previousDesiredWorkers]
                .filter((id): id is string => !!id)
                .filter((id, index, all) => all.indexOf(id) === index)
              current.desiredWorkers = [...currentDesiredWorkers]
                .filter((id): id is string => !!id)
                .filter((id, index, all) => all.indexOf(id) === index)
              return [...acc, previous, current]
            }
          } else {
            current.desiredWorkers = [...current.desiredWorkers]
              .filter((id): id is string => !!id)
              .filter((id, index, all) => all.indexOf(id) === index)
            return [current]
          }
        },
        [] as Array<WorkersRequestCreateFormValues['posts'][number]>,
      )

    const initialValues: WorkersRequestCreateFormValues = {
      meta: {
        startDate: startDateISO,
        endDate: endDateISO,
        resource: undefined,
      },
      interimAgency: workersRequest.interimAgency._id,
      missionData: {
        workPeriods: [],
        service: workersRequest.missionData.service?._id ?? '',
        address: workersRequest.missionData.address,
        complementaryInformations: workersRequest.missionData.complementaryInformations,
      },
      posts,
    }
    return initialValues
  }

  // 4. Fallback: brand new blank workers request.
  const initialValues: WorkersRequestCreateFormValues = {
    meta: {
      startDate: todayISO,
      endDate: endISO,
      resource: undefined,
    },
    interimAgency: '',
    missionData: {
      workPeriods: [],
      service: '',
      address: '',
      complementaryInformations: '',
    },
    posts: [newPostTemplate],
  }
  return initialValues
}
